import { useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import "./ai-analyzer-summary.scss";

/**
 * What the analyzer extracted from the uploaded syllabus (ENG-23218):
 * "We found X learning objectives and Y standards in your syllabus," an
 * optional "View details" expansion listing them, and a coverage warning
 * when the analysis was partial. Purely informational — not required to
 * proceed.
 */
const pluralize = (count, singular) =>
  count === 1 ? singular : `${singular}s`;

export const AiAnalyzerSummary = ({ summary }) => {
  const [expanded, setExpanded] = useState(false);

  if (!summary) return null;

  const objectives = summary.learningObjectives ?? [];
  const standards = summary.standards ?? [];
  const hasDetails = objectives.length > 0 || standards.length > 0;

  return (
    <div className="ai-analyzer-summary">
      <div className="ai-analyzer-summary__counts">
        <FontAwesomeIcon icon="fa-regular fa-file-lines" />
        <span>
          We found <strong>{summary.objectivesMatched}</strong> learning{" "}
          {pluralize(summary.objectivesMatched, "objective")} and{" "}
          <strong>{summary.standardsMatched}</strong>{" "}
          {pluralize(summary.standardsMatched, "standard")} in your syllabus.
        </span>
        {hasDetails && (
          <button
            type="button"
            className="ai-analyzer-summary__toggle"
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? "Hide details" : "View details"}
          </button>
        )}
      </div>

      {summary.partial && (
        <p className="ai-analyzer-summary__partial" role="alert">
          <FontAwesomeIcon icon="fa-regular fa-triangle-exclamation" /> We
          couldn&apos;t process the whole document, so coverage may be
          incomplete. You can still continue.
        </p>
      )}

      {expanded && (
        <div className="ai-analyzer-summary__details">
          {objectives.length > 0 && (
            <div className="ai-analyzer-summary__list">
              <h4>Learning objectives</h4>
              <ul>
                {objectives.map((objective) => (
                  <li key={objective}>{objective}</li>
                ))}
              </ul>
            </div>
          )}
          {standards.length > 0 && (
            <div className="ai-analyzer-summary__list">
              <h4>Standards</h4>
              <ul>
                {standards.map((standard) => (
                  <li key={standard}>{standard}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
